// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createHook, createSubscriber } from '@atlassian/react-sweet-state';
import type { UserCapabilities } from '../../common/types.tsx';
import { actions, type Actions } from './actions/index.tsx';
import {
	getArticleId,
	getDraftShareId,
	getIsFetchingDraftDetails,
	getPermittedSpaces,
	getLinkedSpaces,
	getRedirectionData,
	getUserCapabilities,
	getCategoriesData,
	getKnowledgeCommonFlags,
	getCreateCategoryData,
	getCommonFlags,
	getCategory,
	getSuggestedTopics,
	getPageNumber,
	getConnectionStatus,
	getIsKbFeedbackBannerDismissed,
} from './selectors/index.tsx';
import type { State, UserCapabilitiesData, CreateCategoryState } from './types.tsx';

export const initialUserCapabilities: UserCapabilities = {
	hasConfluenceLicense: false,
	articleUserCapabilities: {
		canCreateArticle: false,
	},
	spaceUserCapabilities: {
		editableSpaces: [],
		readableSpaces: [],
	},
};

export const initialCreateCategoryData: CreateCategoryState = {
	isSubmitting: false,
	formValues: {
		name: '',
		description: '',
	},
	error: {
		formError: undefined,
		fieldErrors: undefined,
	},
	createdCategory: null,
};

export const initialState: State = {
	connectionStatus: null,
	draftShareId: '',
	articleId: '',
	published: false,
	closed: false,
	deleted: false,
	redirectionData: {
		onPublishRedirectUrl: '',
		onCloseRedirectUrl: '',
	},
	isFetchingDraftDetails: false,
	spacesPermitted: { isLoading: false, data: null, error: null, totalSize: 0 },
	permittedSpaces: { isLoading: false, data: null, error: null, totalSize: 0 },
	userCapabilities: {
		isLoading: false,
		error: null,
		data: { ...initialUserCapabilities },
	},
	linkedSpaces: { isLoading: false, data: null, error: null, totalSize: 0 },
	showConfluenceCreatePageError: false,
	categoriesData: {
		isLoading: true,
		categories: [],
		isCategoriesSupported: false,
		error: null,
	},
	createCategoryData: { ...initialCreateCategoryData },
	spaceKey: '',
	flags: [],
	topics: {
		data: [],
		loading: false,
		error: null,
	},
	page: 1,
	isKbFeedbackBannerDismissed: false,
};

export const store = createStore<State, Actions>({
	initialState,
	actions,
	name: 'KnowledgeCommonStore',
});

export const KnowledgeCommonSubscriber = createSubscriber<State, Actions>(store);

export const UserCapabilitiesSubscriber = createSubscriber<
	State,
	Actions,
	UserCapabilitiesData,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Record<any, any>
>(store, {
	displayName: 'UserCapabilitiesSubscriber',
	selector: getUserCapabilities,
});

export const useConnectionStatus = createHook(store, {
	selector: getConnectionStatus,
});

export const useCommonFlags = createHook(store, {
	selector: getCommonFlags,
});

export const useFlagData = createHook(store, {
	selector: getKnowledgeCommonFlags,
});

export const useGetCategory = createHook(store, {
	selector: getCategory,
});

export const useDraftShareId = createHook(store, {
	selector: getDraftShareId,
});

export const useArticleId = createHook(store, {
	selector: getArticleId,
});

export const useIsFetchingDraftDetails = createHook(store, {
	selector: getIsFetchingDraftDetails,
});

export const useRedirectionData = createHook(store, {
	selector: getRedirectionData,
});

export const useUserCapabilities = createHook(store, {
	selector: getUserCapabilities,
});

export const useIsPermittedSpaces = createHook(store, {
	selector: getPermittedSpaces,
});

export const useLinkedSpaces = createHook(store, {
	selector: getLinkedSpaces,
});

export const useCategoriesData = createHook(store, {
	selector: getCategoriesData,
});

export const useCreateCategoryData = createHook(store, {
	selector: getCreateCategoryData,
});

export const useSuggestedTopics = createHook(store, {
	selector: getSuggestedTopics,
});

export const usePageNumber = createHook(store, {
	selector: getPageNumber,
});

export const useIsKbFeedbackBannerDismissed = createHook(store, {
	selector: getIsKbFeedbackBannerDismissed,
});
